import { useQuery } from '@apollo/client';

import { MultiGetSkillsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/MultiGetSkillsQuery';
import type {
  MultiGetSkillsResponse,
  MultiGetSkillsVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/MultiGetSkillsQuery';
import UserSkillScoresBySkillIdsQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/UserSkillScoresBySkillIds.graphql';
import type {
  UserSkillScoresBySkillIdsQuery as UserSkillScoresBySkillIdsQueryData,
  UserSkillScoresBySkillIdsQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/UserSkillScoresBySkillIds';

type Props = {
  skillIds: string[];
  userId?: number;
};

const useUserSkillScores = ({ skillIds, userId }: Props) => {
  const { data: skillScoreData, loading: isLoadingSkillScore } = useQuery<
    UserSkillScoresBySkillIdsQueryData,
    UserSkillScoresBySkillIdsQueryVariables
  >(UserSkillScoresBySkillIdsQuery, {
    variables: {
      skillIds,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: !userId,
  });

  const skillScoresList = skillScoreData?.SkillScores?.userSkillScoresBySkillIds ?? [];
  const skillsScoresById: { [skillId: string]: number } = {};
  skillScoresList.forEach((skillScore) => {
    skillsScoresById[skillScore.skill.id] = skillScore.readableScore ?? 0;
  });
  const skillsWithNoScore = skillIds.filter((id) => !skillsScoresById[id]);

  // We only need to confirm that a skill is eligible for scoring if no score was returned for it
  const { data: skillData, loading: isLoadingSkillData } = useQuery<MultiGetSkillsResponse, MultiGetSkillsVariables>(
    MultiGetSkillsQuery,
    {
      variables: {
        input: {
          skillIds: skillsWithNoScore,
        },
      },
      skip: isLoadingSkillScore || !userId || !skillsWithNoScore.length,
    }
  );

  const eligibleSkillsForScoring = (skillData?.MultiGetSkillsResponse?.skills ?? []).filter(
    (skill) => skill.isEligibleForScoring
  );

  eligibleSkillsForScoring.forEach((eligibleSkill) => {
    skillsScoresById[eligibleSkill.skillId] = 0;
  });

  return {
    isLoadingScore: isLoadingSkillScore || isLoadingSkillData,
    skillScores: skillsScoresById,
  };
};

export default useUserSkillScores;
